import { ZuruflexApiError } from '@zuruflex/api-client';
import {
  Alert,
  Button,
  FormField,
  GoogleButton,
  Input,
  PasswordInput,
  Logo,
  ThemeToggle,
  useDevCredentials,
} from '@zuruflex/ui';
import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api, googleClientId } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

export function SignInPage() {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const { signIn, signInWithGoogle } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The dev login panel (`@zuruflex/ui` DevAccounts) types into these fields
  // and submits, rather than signing in behind the form's back, so what
  // lands here is exactly what a person would have typed and pressed. The
  // credentials go straight to `signIn` rather than through the state just
  // set, which this render has not seen yet.
  useDevCredentials((credentials) => {
    setEmail(credentials.email);
    setPassword(credentials.password);
    void attempt(() => signIn(credentials.email, credentials.password));
  });

  /**
   * The deployment decides whether Google is on. Asking the API rather than
   * only checking the build-time variable means a missing server-side
   * client id cannot leave a button that always fails.
   */
  const { data: methods } = useQuery({
    queryKey: ['auth-config'],
    queryFn: () => api.authConfig(),
    staleTime: Infinity,
  });
  const googleEnabled = Boolean(googleClientId) && methods?.google !== false;

  async function attempt(action: () => Promise<void>) {
    setError(null);
    setBusy(true);
    try {
      await action();
      // No navigation: the router swaps the whole tree once the session
      // exists, so pushing a route here would race with that.
    } catch (cause) {
      setError(cause instanceof ZuruflexApiError ? cause.message : t('common:error.unexpected'));
    } finally {
      setBusy(false);
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    void attempt(() => signIn(email, password));
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-surface px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="flex justify-end">
          <ThemeToggle />
        </div>
        <Logo className="mx-auto h-8" />

        <h1 className="mt-8 text-center text-xl font-semibold text-text">{t('signIn.title')}</h1>
        <p className="mt-1 text-center text-sm text-text-muted">{t('signIn.subtitle')}</p>

        {error ? (
          <Alert variant="danger" className="mt-6">
            {error}
          </Alert>
        ) : null}

        <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
          <FormField label={t('common:field.email')} required>
            {(field) => (
              <Input
                {...field}
                type="email"
                value={email}
                autoComplete="username"
                autoFocus
                required
                onChange={(event) => setEmail(event.target.value)}
              />
            )}
          </FormField>

          <FormField label={t('common:field.password')} required>
            {(field) => (
              <PasswordInput
                {...field}
                value={password}
                autoComplete="current-password"
                required
                onChange={(event) => setPassword(event.target.value)}
                showLabel={t('common:action.showPassword')}
                hideLabel={t('common:action.hidePassword')}
              />
            )}
          </FormField>

          <Button type="submit" disabled={busy} className="mt-1 w-full">
            {busy ? t('common:state.working') : t('signIn.submit')}
          </Button>
        </form>

        {googleEnabled ? (
          <>
            <div className="my-6 flex items-center gap-3">
              <span className="h-px flex-1 bg-border-default" />
              <span className="text-xs uppercase tracking-wide text-text-muted">
                {t('common:word.or')}
              </span>
              <span className="h-px flex-1 bg-border-default" />
            </div>
            <GoogleButton
              clientId={googleClientId}
              locale={i18n.language}
              onCredential={(accessToken) => void attempt(() => signInWithGoogle({ accessToken }))}
            />
          </>
        ) : null}

        <p className="mt-8 text-center text-xs text-text-muted">{t('signIn.provisioned')}</p>
      </div>
    </main>
  );
}
